import React, { useState, useEffect, useRef, useCallback } from "react";
import { CommonInput } from "@/components/ui/common-input";
import { CommonSelect } from "@/components/ui/common-select";
import { CommonButton } from "@/components/ui/common-button";
import { DateTimeInput } from "@/components/ui/DateTimeInput";
import { CommonFileUpload } from "@/components/ui/common-file-upload";
import { useToast } from "@/components/ui/use-toast";
import GoogleMapSelector from "./GoogleMapSelector";
import masterDataService from "@/services/masterDataService";
import productService from "@/services/productService";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/hooks/useTheme";
import tripService from "@/services/tripService";
import { Plus, X } from "lucide-react";
import Loader from "@/components/ui/loader";

const ALLOWED_DOCUMENT_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx'];
const isAllowedDocument = (file) => {
  if (!file) return false;
  const fileName = file.name?.toLowerCase() || '';
  return ALLOWED_DOCUMENT_EXTENSIONS.some(ext => fileName.endsWith(ext));
};

const toSafeIsoString = (val) => {
  if (!val) return undefined;
  const d = new Date(val);
  return !isNaN(d.getTime()) ? d.toISOString() : undefined;
};

const AddTripForm = ({ onCreateTrip, onCancel, isSubmitting }) => {
  const [internalSubmitting, setInternalSubmitting] = React.useState(false);

  const [consigneeStateDistricts, setConsigneeStateDistricts] = useState({});
  const [loadingConsigneeDistricts, setLoadingConsigneeDistricts] = useState({});
  const { toast } = useToast();
  const { userProfile } = useAuth();
  const { theme } = useTheme();

  const [distributorProductRows, setDistributorProductRows] = useState([
    {
      id: Date.now(),
      distributor_id: "",
      product_type_id: "",
      product_quantity: "",
      consignee_state_id: "",
      consignee_district_id: "",
      consignee_name: "",
      consignee_contact_person: "",
      consignee_phone: "",
      consignee_email: "",
      consignee_address: "",
      consignee_delivery_instructions: "",
      trip_photo: null,
      state_id: "",
      district_id: "",
      end_latitude: "",
      end_longitude: "",
      invoice_number: "",
      invoice_file: null,
      dc_number: "",
      estimated_kms: "",
      eway_bill_number: "",
      eway_bill_date: ""
    }
  ]);

  const [formData, setFormData] = useState({
    driver_id: "",
    vehicle_id: "",
    transporter_id: "",
    scheduled_start_time: "",
    scheduled_end_time: "",
    start_latitude: "",
    start_longitude: "",
    port_id: "",
  });

  const [errors, setErrors] = useState({});
  const [masterData, setMasterData] = useState({
    drivers: [],
    vehicles: [],
    transporters: [],
    distributors: [],
    retailers: [],
    productTypes: [],
    ports: [],
    states: [],
    districts: []
  });
  const [loading, setLoading] = useState({
    page: true,
    drivers: false,
    vehicles: false,
    transporters: false,
    distributors: false,
    productTypes: false,
    ports: false,
    states: false,
    districts: false
  });
  const isInitialLoadRef = useRef(true);


  const [districtDistributors, setDistrictDistributors] = useState({});
  const [districtSearchTerms, setDistrictSearchTerms] = useState({});


  const [driverSearchTerm, setDriverSearchTerm] = useState('');
  const driverSearchTimeoutRef = useRef(null);
  const [driversSkip, setDriversSkip] = useState(0);
  const [driversHasMore, setDriversHasMore] = useState(true);
  const [loadingMoreDrivers, setLoadingMoreDrivers] = useState(false);


  const [vehicleSearchTerm, setVehicleSearchTerm] = useState('');
  const vehicleSearchTimeoutRef = useRef(null);
  const [vehiclesSkip, setVehiclesSkip] = useState(0);
  const [vehiclesHasMore, setVehiclesHasMore] = useState(true);
  const [loadingMoreVehicles, setLoadingMoreVehicles] = useState(false);

  const [transporterSearchTerm, setTransporterSearchTerm] = useState('');
  const transporterSearchTimeoutRef = useRef(null);
  const [transportersSkip, setTransportersSkip] = useState(0);
  const [transportersHasMore, setTransportersHasMore] = useState(true);
  const [loadingMoreTransporters, setLoadingMoreTransporters] = useState(false);

  useEffect(() => {
    if (userProfile) {
      const uRole = userProfile.role?.toLowerCase();
      if (uRole === 'transporter' && userProfile.id) {
        setFormData(prev => ({
          ...prev,
          transporter_id: userProfile.id,
        }));
      }
    }
  }, [userProfile]);




  const fetchDistricts = async (stateId) => {
    if (!stateId) return;

    try {
      setLoading(prev => ({ ...prev, districts: true }));
      const districts = await masterDataService.getDistrictsByState(stateId, true);
      setMasterData(prev => ({ ...prev, districts }));
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load districts.",
        variant: "destructive",
      });
    } finally {
      setLoading(prev => ({ ...prev, districts: false }));
    }
  };


  const fetchConsigneeDistricts = async (stateId) => {
    if (!stateId || consigneeStateDistricts[stateId]) return;
    try {
      setLoadingConsigneeDistricts(prev => ({ ...prev, [stateId]: true }));
      const districts = await masterDataService.getDistrictsByState(stateId, true);
      setConsigneeStateDistricts(prev => ({
        ...prev,
        [stateId]: Array.isArray(districts) ? districts : []
      }));
    } catch (error) {
      console.error('Failed to load districts for consignee state:', error);
    } finally {
      setLoadingConsigneeDistricts(prev => ({ ...prev, [stateId]: false }));
    }
  };




  const fetchDistributorsByDistrict = async (districtId, searchTerm = '') => {
    if (!districtId) return;

    try {
      setLoading(prev => ({ ...prev, distributors: true }));
      const distributors = await masterDataService.getDistributorsByDistrict(
        districtId,
        searchTerm,
        0,
        50,
        false
      );

      setDistrictDistributors(prev => ({
        ...prev,
        [districtId]: Array.isArray(distributors) ? distributors : []
      }));
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load distributors for this district.",
        variant: "destructive",
      });
      setDistrictDistributors(prev => ({
        ...prev,
        [districtId]: []
      }));
    } finally {
      setLoading(prev => ({ ...prev, distributors: false }));
    }
  };

  const driverOptions = masterData.drivers.map(driver => ({
    value: driver.id,
    label: `${driver.user?.username || driver.name || driver.id} (${driver.license_number || 'No License'})`
  }));

  const vehicleOptions = masterData.vehicles.map(vehicle => ({
    value: vehicle.id,
    label: `${vehicle.vehicle_number || vehicle.name || vehicle.id} (${vehicle.vehicle_type || 'No Type'})`
  }));

  const transporterOptions = masterData.transporters.map(transporter => ({
    value: transporter.id,
    label: transporter.company_name || transporter.name || transporter.id
  }));

  const productTypeOptions = masterData.productTypes.map(product => ({
    value: product.id,
    label: `${product.name || product.id} (${product.unit_of_measure || 'No Unit'})`
  }));

  const portOptions = masterData.ports.map(port => ({
    value: port.id,
    label: `${port.port_name || port.name || port.id} (${port.port_code || 'No Code'})`
  }));

  const stateOptions = masterData.states.map(state => ({
    value: state.id,
    label: state.state_name || state.name || state.id
  }));

  const districtOptions = masterData.districts.map(district => ({
    value: district.id,
    label: district.district_name || district.name || district.id
  }));

  const addDistributorProductRow = () => {
    const newRow = {
      id: Date.now(),
      distributor_id: "",
      product_type_id: "",
      product_quantity: "",
      consignee_state_id: "",
      consignee_district_id: "",
      consignee_name: "",
      consignee_contact_person: "",
      consignee_phone: "",
      consignee_email: "",
      consignee_address: "",
      consignee_delivery_instructions: "",
      trip_photo: null,
      state_id: "",
      district_id: "",
      end_latitude: "",
      end_longitude: "",
      invoice_number: "",
      invoice_file: null,
      dc_number: "",
      estimated_kms: "",
      eway_bill_number: "",
      eway_bill_date: ""
    };

    setDistributorProductRows(prev => [...prev, newRow]);
  };

  const removeDistributorProductRow = (id) => {
    if (distributorProductRows.length > 1) {
      setDistributorProductRows(prev => prev.filter(row => row.id !== id));
    }
  };

  const updateDistributorProductRow = (id, field, value) => {
    setDistributorProductRows(prev => prev.map(row =>
      row.id === id ? { ...row, [field]: value } : row
    ));

    if (field === 'state_id') {
      if (value) {
        fetchDistricts(value);
      } else {
        setDistributorProductRows(prev =>
          prev.map(row =>
            row.id === id ? { ...row, district_id: "", distributor_id: "" } : row
          )
        );
      }
    }

    if (field === 'district_id') {
      if (value) {
        fetchDistributorsByDistrict(value);
      } else {
        setDistributorProductRows(prev =>
          prev.map(row =>
            row.id === id ? { ...row, distributor_id: "" } : row
          )
        );
      }
    }
  };

  const handleDistributorSearch = (districtId, searchTerm) => {
    setDistrictSearchTerms(prev => ({
      ...prev,
      [districtId]: searchTerm
    }));

    clearTimeout(window.distributorSearchTimeout);
    window.distributorSearchTimeout = setTimeout(() => {
      fetchDistributorsByDistrict(districtId, searchTerm);
    }, 500);
  };

  // Helper: clear a specific error field from a row
  const clearRowError = (rowId, ...fields) => {
    setErrors(prev => {
      if (!Array.isArray(prev.distributor_product_rows)) return prev;
      const idx = distributorProductRows.findIndex(r => r.id === rowId);
      if (idx === -1) return prev;
      const updated = [...prev.distributor_product_rows];
      const rowErr = { ...updated[idx] };
      fields.forEach(f => delete rowErr[f]);
      updated[idx] = rowErr;
      return { ...prev, distributor_product_rows: updated };
    });
  };

  const handleRowFileChange = (rowId, file) => {
    updateDistributorProductRow(rowId, "trip_photo", file);
    clearRowError(rowId, "trip_photo");
  };

  const handleRowInvoiceFileChange = (rowId, file) => {
    setDistributorProductRows(prev => prev.map(r =>
      r.id === rowId ? { ...r, invoice_file: file } : r
    ));
    clearRowError(rowId, "invoice_file");
  };


  const validateForm = () => {
    const newErrors = {};

    const userRole = userProfile?.role?.toLowerCase();
    if (!formData.driver_id) {
      newErrors.driver_id = "Driver is required";
    }

    if (!formData.vehicle_id) {
      newErrors.vehicle_id = "Vehicle is required";
    }

    if (userRole !== 'transporter' && !formData.transporter_id) {
      newErrors.transporter_id = "Transporter is required";
    }

    if (!['region_head', 'account_head', 'field_officer', 'distributor'].includes(userRole) && !formData.port_id) {
      newErrors.port_id = "Port is required";
    }

    const rowErrors = [];
    let hasValidRow = false;

    distributorProductRows.forEach((row, index) => {
      const rowError = {};

      if (!row.state_id) {
        rowError.state_id = "State is required";
      }

      if (!row.district_id) {
        rowError.district_id = "District is required";
      }

      if (!row.distributor_id || row.distributor_id === 'no-data' || row.distributor_id === 'loading') {
        rowError.distributor_id = userProfile?.role === 'distributor' ? "Retailer is required" : "Distributor is required";
      }

      if (!row.product_type_id) {
        rowError.product_type_id = "Product type is required";
      }

      if (!row.product_quantity) {
        rowError.product_quantity = "Quantity is required";
      } else if (isNaN(row.product_quantity) || parseFloat(row.product_quantity) <= 0) {
        rowError.product_quantity = "Product quantity must be a positive number";
      }

      if (!row.consignee_state_id) {
        rowError.consignee_state_id = "State is required";
      }

      if (!row.consignee_district_id) {
        rowError.consignee_district_id = "District is required";
      }

      if (!row.consignee_name.trim()) {
        rowError.consignee_name = "Consignee name is required";
      }

      if (!row.consignee_address.trim()) {
        rowError.consignee_address = "Consignee address is required";
      }

      if (!row.consignee_phone.trim()) {
        rowError.consignee_phone = "Consignee phone is required";
      } else if (row.consignee_phone.trim().length !== 10) {
        rowError.consignee_phone = "Phone number must be exactly 10 digits";
      } else if (!/^[0-9]{10}$/.test(row.consignee_phone.trim())) {
        rowError.consignee_phone = "Phone number must contain exactly 10 digits";
      }

      if (row.end_latitude === "" || row.end_longitude === "") {
        rowError.end_location = "End location is required";
      } else {
        const lat = parseFloat(row.end_latitude);
        const lng = parseFloat(row.end_longitude);
        if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
          rowError.end_location = "Invalid end location coordinates";
        }
      }

      if (!row.invoice_file) {
        rowError.invoice_file = "Invoice document is required";
      } else if (!isAllowedDocument(row.invoice_file)) {
        rowError.invoice_file = "Only PDF, DOC, and XLS files are allowed";
      }

      if (!row.invoice_number?.trim()) {
        rowError.invoice_number = "Invoice number is required";
      }

      if (!row.dc_number?.trim()) {
        rowError.dc_number = "DC number is required";
      }

      if (!row.eway_bill_number?.trim()) {
        rowError.eway_bill_number = "E-way bill number is required";
      }

      if (!row.trip_photo) {
        rowError.trip_photo = "E-way bill document is required";
      } else if (!isAllowedDocument(row.trip_photo)) {
        rowError.trip_photo = "Only PDF, DOC, and XLS files are allowed";
      }

      if (!row.estimated_kms) {
        rowError.estimated_kms = "Estimated KMs is required";
      } else if (isNaN(row.estimated_kms) || parseFloat(row.estimated_kms) <= 0) {
        rowError.estimated_kms = "Estimated KMs must be a positive number";
      }

      if (Object.keys(rowError).length > 0) {
        rowErrors[index] = rowError;
      } else {
        hasValidRow = true;
      }
    });

    if (!hasValidRow) {
      newErrors.distributor_product_rows = "At least one valid distributor-product combination is required";
    }

    if (rowErrors.length > 0) {
      newErrors.distributor_product_rows = rowErrors;
    }

    if (!formData.scheduled_start_time) {
      newErrors.scheduled_start_time = "Scheduled start time is required";
    }

    if (!formData.scheduled_end_time) {
      newErrors.scheduled_end_time = "Scheduled end time is required";
    }

    if (formData.start_latitude === "" || formData.start_longitude === "") {
      newErrors.start_location = "Start location is required";
    }

    if (formData.scheduled_start_time && formData.scheduled_end_time) {
      const startTime = new Date(formData.scheduled_start_time);
      const endTime = new Date(formData.scheduled_end_time);

      if (endTime <= startTime) {
        newErrors.scheduled_end_time = "End time must be after start time";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleStartLocationSelect = (location) => {
    setFormData((prev) => ({
      ...prev,
      start_latitude: location.lat.toString(),
      start_longitude: location.lng.toString(),
    }));

    if (errors.start_location) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.start_location;
        return newErrors;
      });
    }
  };

  const fetchDrivers = useCallback(async (search = '') => {
    try {
      setLoading(prev => ({ ...prev, drivers: true }));
      const drivers = await masterDataService.getDrivers(0, 10, false, false, search);
      setMasterData(prev => ({ ...prev, drivers }));
      setDriversSkip(10);
      setDriversHasMore(drivers.length === 10);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load drivers.",
        variant: "destructive",
      });
    } finally {
      setLoading(prev => ({ ...prev, drivers: false }));
    }
  }, [toast]);

  const loadMoreDrivers = async () => {
    if (loadingMoreDrivers || !driversHasMore) return;
    try {
      setLoadingMoreDrivers(true);
      const nextDrivers = await masterDataService.getDrivers(
        driversSkip,
        10,
        false,
        false,
        driverSearchTerm
      );
      setMasterData(prev => ({
        ...prev,
        drivers: [...prev.drivers, ...nextDrivers]
      }));
      setDriversSkip(prev => prev + 10);
      setDriversHasMore(nextDrivers.length === 10);
    } catch (error) {
      console.error("Failed to load more drivers", error);
    } finally {
      setLoadingMoreDrivers(false);
    }
  };

  const fetchVehicles = useCallback(async (search = '') => {
    try {
      setLoading(prev => ({ ...prev, vehicles: true }));
      const vehicles = await masterDataService.getVehicles(0, 10, false, false, search);
      setMasterData(prev => ({ ...prev, vehicles }));
      setVehiclesSkip(10);
      setVehiclesHasMore(vehicles.length === 10);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load vehicles.",
        variant: "destructive",
      });
    } finally {
      setLoading(prev => ({ ...prev, vehicles: false }));
    }
  }, [toast]);

  const loadMoreVehicles = async () => {
    if (loadingMoreVehicles || !vehiclesHasMore) return;
    try {
      setLoadingMoreVehicles(true);
      const nextVehicles = await masterDataService.getVehicles(
        vehiclesSkip,
        10,
        false,
        false,
        vehicleSearchTerm
      );
      setMasterData(prev => ({
        ...prev,
        vehicles: [...prev.vehicles, ...nextVehicles]
      }));
      setVehiclesSkip(prev => prev + 10);
      setVehiclesHasMore(nextVehicles.length === 10);
    } catch (error) {
      console.error("Failed to load more vehicles", error);
    } finally {
      setLoadingMoreVehicles(false);
    }
  };

  const fetchTransporters = useCallback(async (search = '') => {
    try {
      setLoading(prev => ({ ...prev, transporters: true }));
      const transporters = await masterDataService.getTransporters(0, 10, false, search);
      setMasterData(prev => ({ ...prev, transporters }));
      setTransportersSkip(10);
      setTransportersHasMore(transporters.length === 10);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load transporters.",
        variant: "destructive",
      });
    } finally {
      setLoading(prev => ({ ...prev, transporters: false }));
    }
  }, [toast]);

  const loadMoreTransporters = async () => {
    if (loadingMoreTransporters || !transportersHasMore) return;
    try {
      setLoadingMoreTransporters(true);
      const nextTransporters = await masterDataService.getTransporters(
        transportersSkip,
        10,
        false,
        transporterSearchTerm
      );
      setMasterData(prev => ({
        ...prev,
        transporters: [...prev.transporters, ...nextTransporters]
      }));
      setTransportersSkip(prev => prev + 10);
      setTransportersHasMore(nextTransporters.length === 10);
    } catch (error) {
      console.error("Failed to load more transporters", error);
    } finally {
      setLoadingMoreTransporters(false);
    }
  };

  useEffect(() => {
    if (isInitialLoadRef.current) return;
    if (driverSearchTimeoutRef.current) {
      clearTimeout(driverSearchTimeoutRef.current);
    }

    driverSearchTimeoutRef.current = setTimeout(() => {
      fetchDrivers(driverSearchTerm);
    }, 300);

    return () => {
      if (driverSearchTimeoutRef.current) {
        clearTimeout(driverSearchTimeoutRef.current);
      }
    };
  }, [driverSearchTerm, fetchDrivers]);

  useEffect(() => {
    if (isInitialLoadRef.current) return;
    if (vehicleSearchTimeoutRef.current) {
      clearTimeout(vehicleSearchTimeoutRef.current);
    }

    vehicleSearchTimeoutRef.current = setTimeout(() => {
      fetchVehicles(vehicleSearchTerm);
    }, 300);

    return () => {
      if (vehicleSearchTimeoutRef.current) {
        clearTimeout(vehicleSearchTimeoutRef.current);
      }
    };
  }, [vehicleSearchTerm, fetchVehicles]);

  useEffect(() => {
    if (isInitialLoadRef.current) return;
    if (transporterSearchTimeoutRef.current) {
      clearTimeout(transporterSearchTimeoutRef.current);
    }

    transporterSearchTimeoutRef.current = setTimeout(() => {
      fetchTransporters(transporterSearchTerm);
    }, 300);

    return () => {
      if (transporterSearchTimeoutRef.current) {
        clearTimeout(transporterSearchTimeoutRef.current);
      }
    };
  }, [transporterSearchTerm, fetchTransporters]);

  const fetchMasterData = async () => {
    try {
      setLoading(prev => ({ ...prev, page: true }));

      try {
        const distributors = await masterDataService.getDistributors(0, 100, false);
        setMasterData(prev => ({ ...prev, distributors }));
      } catch (err) {
        console.error("Failed to load distributors on mount:", err);
      }

      try {
        const retailers = await masterDataService.getRetailers(0, 100, false);
        setMasterData(prev => ({
          ...prev,
          retailers: Array.isArray(retailers) ? retailers : []
        }));
      } catch (err) {
        console.error("Failed to load retailers on mount:", err);
      }

      setLoading(prev => ({ ...prev, productTypes: true }));
      const productTypes = await productService.getProducts(0, 100);
      setMasterData(prev => ({ ...prev, productTypes }));

      const role = userProfile?.role?.toLowerCase();
      if (role !== 'distributor') {
        setLoading(prev => ({ ...prev, ports: true }));
        const ports = await masterDataService.getPorts(0, 100);
        setMasterData(prev => ({ ...prev, ports }));
      }

      const states = await masterDataService.getStates(true);
      setMasterData(prev => ({ ...prev, states }));

      await fetchDrivers('');
      await fetchVehicles('');
      await fetchTransporters('');

      isInitialLoadRef.current = false;
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load master data. Some dropdowns may not be populated.",
        variant: "destructive",
      });
    } finally {
      setLoading(prev => ({
        ...prev,
        page: false
      }));
    }
  };

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    return () => {
      if (driverSearchTimeoutRef.current) {
        clearTimeout(driverSearchTimeoutRef.current);
      }
      if (vehicleSearchTimeoutRef.current) {
        clearTimeout(vehicleSearchTimeoutRef.current);
      }
      if (transporterSearchTimeoutRef.current) {
        clearTimeout(transporterSearchTimeoutRef.current);
      }
    };
  }, []);
  const getStateName = (id) => {
    const s = masterData.states?.find(st => st.id == id);
    return s ? (s.state_name || s.name) : id;
  };

  const getCityName = (stateId, distId) => {
    const list = consigneeStateDistricts[stateId] || masterData.districts || [];
    const d = list.find(dt => dt.id == distId);
    return d ? (d.district_name || d.name) : distId;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setInternalSubmitting(true);
      try {
        const items = distributorProductRows
          .filter(row => row.distributor_id && row.product_type_id && row.product_quantity &&
            row.distributor_id !== 'no-data' && row.distributor_id !== 'loading')
          .map((row, index) => ({
            distributor_id: userProfile?.role === 'distributor' ? undefined : (row.distributor_id || undefined),
            retailer_id: userProfile?.role === 'distributor' ? (row.distributor_id || undefined) : undefined,
            product_type_id: row.product_type_id || undefined,
            product_quantity: row.product_quantity ? parseFloat(row.product_quantity) : undefined,
            consignee_name: row.consignee_name || undefined,
            consignee_contact_person: row.consignee_contact_person || undefined,
            consignee_phone: row.consignee_phone || undefined,
            consignee_email: row.consignee_email || undefined,
            consignee_address: row.consignee_address || undefined,
            consignee_delivery_instructions: row.consignee_delivery_instructions || undefined,
            consignee_state: getStateName(row.consignee_state_id) || undefined,
            consignee_city: getCityName(row.consignee_state_id, row.consignee_district_id) || undefined,
            end_latitude: row.end_latitude ? parseFloat(row.end_latitude) : undefined,
            end_longitude: row.end_longitude ? parseFloat(row.end_longitude) : undefined,
            invoice_number: row.invoice_number || undefined,
            dc_number: row.dc_number || undefined,
            estimated_kms: row.estimated_kms ? parseFloat(row.estimated_kms) : undefined,
            eway_bill_number: row.eway_bill_number?.trim() || undefined,
            eway_bill_date: toSafeIsoString(row.eway_bill_date),
            item_sequence: index + 1
          }));

        const userRole = userProfile?.role?.toLowerCase();
        const isDistributorUser = userRole === 'distributor';

        const tripData = {
          transporter_id: userRole === 'transporter' ? userProfile.id : (formData.transporter_id || undefined),
          driver_id: formData.driver_id || undefined,
          vehicle_id: formData.vehicle_id || undefined,
          port_id: isDistributorUser ? undefined : (formData.port_id || undefined),
          scheduled_start_time: formData.scheduled_start_time,
          scheduled_end_time: formData.scheduled_end_time,
          start_latitude: parseFloat(formData.start_latitude),
          start_longitude: parseFloat(formData.start_longitude),
          movement: isDistributorUser ? 'Secondary' : 'Primary',
          movementType: 'ROAD',
          items: items
        };

        if (tripData.scheduled_start_time) {
          tripData.scheduled_start_time = toSafeIsoString(tripData.scheduled_start_time);
        }

        if (tripData.scheduled_end_time) {
          tripData.scheduled_end_time = toSafeIsoString(tripData.scheduled_end_time);
        }

        const createdTrip = await onCreateTrip(tripData);

        if (createdTrip && createdTrip.id && createdTrip.items) {
          try {
            const fileUploadPromises = [];

            const rowIdToTripItemId = {};
            distributorProductRows.forEach((row, index) => {
              if (createdTrip.items[index]) {
                rowIdToTripItemId[row.id] = createdTrip.items[index].id;
              }
            });

            distributorProductRows.forEach(row => {
              if (row.trip_photo && row.trip_photo instanceof File && rowIdToTripItemId[row.id]) {
                const tripItemId = rowIdToTripItemId[row.id];
                const fileType = 'eway_bill';
                const uploadPromise = tripService.uploadTripItemFile(tripItemId, row.trip_photo, fileType, `Eway bill for trip item ${tripItemId}`);
                fileUploadPromises.push(uploadPromise);
              }
              if (row.invoice_file && row.invoice_file instanceof File && rowIdToTripItemId[row.id]) {
                const tripItemId = rowIdToTripItemId[row.id];
                const fileType = 'invoice';
                const uploadPromise = tripService.uploadTripItemFile(tripItemId, row.invoice_file, fileType, `Invoice for trip item ${tripItemId}`);
                fileUploadPromises.push(uploadPromise);
              }
            });

            if (fileUploadPromises.length > 0) {
              await Promise.all(fileUploadPromises);
            }

            toast({
              title: "Trip Created",
              description: "Trip has been successfully created with file uploads.",
            });
            onCancel();
          } catch (error) {
            toast({
              title: "Trip Created with File Upload Issues",
              description: "Trip was created but some files failed to upload. Please check the trip details.",
              variant: "destructive",
            });
          }
        } else if (createdTrip && createdTrip.id) {
          toast({
            title: "Trip Created",
            description: "Trip has been successfully created.",
          });
          onCancel();
        }
      } finally {
        setInternalSubmitting(false);
      }
    }
  };

  const handleCancel = () => {
    setDistributorProductRows([{
      id: Date.now(),
      distributor_id: "",
      product_type_id: "",
      product_quantity: "",
      consignee_name: "",
      consignee_contact_person: "",
      consignee_phone: "",
      consignee_email: "",
      consignee_address: "",
      consignee_delivery_instructions: "",
      trip_photo: null,
      state_id: "",
      district_id: "",
      end_latitude: "",
      end_longitude: "",
      invoice_number: "",
      invoice_file: null,
      dc_number: "",
      estimated_kms: "",
      eway_bill_number: "",
      eway_bill_date: ""
    }]);
    setFormData({
      driver_id: "",
      vehicle_id: "",
      transporter_id: "",
      scheduled_start_time: "",
      scheduled_end_time: "",
      start_latitude: "",
      start_longitude: "",
      port_id: "",
    });
    setErrors({});
    onCancel();
  };

  const userRole = userProfile?.role?.toLowerCase();
  const isDistributorUser = userRole === 'distributor';
  const showTransporterDropdown = userRole === 'ipl_super_admin' || userRole === 'port_admin' || isDistributorUser || ['region_head', 'account_head', 'field_officer'].includes(userRole);
  const showPortField = !isDistributorUser;

  if (loading.page) {
    return (
      <div className="flex justify-center items-center h-96">
        <Loader size="lg" message="Loading form data..." />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        <div>
          <div className="space-y-4">
            <GoogleMapSelector
              title="Select Start Location"
              onLocationSelect={handleStartLocationSelect}
              initialLat={formData.start_latitude ? parseFloat(formData.start_latitude) : null}
              initialLng={formData.start_longitude ? parseFloat(formData.start_longitude) : null}
            />
            {errors.start_location && (
              <p className="text-sm text-destructive mt-1">
                {errors.start_location}
              </p>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {showTransporterDropdown ? (
              <CommonSelect
                label="Transporter"
                id="transporter_id"
                value={formData.transporter_id}
                onValueChange={(value) => handleChange("transporter_id", value)}
                options={transporterOptions}
                error={errors.transporter_id}
                required
                placeholder="Select transporter"
                searchable
                loading={loading.transporters}
                onSearchChange={setTransporterSearchTerm}
                searchValue={transporterSearchTerm}
                hasMore={transportersHasMore}
                loadingMore={loadingMoreTransporters}
                onLoadMore={loadMoreTransporters}
              />
            ) : (
              <div className="flex flex-col space-y-2">
                <label className="text-sm font-medium text-gray-700">Transporter</label>
                <div className="px-3 py-2 text-sm rounded-md border border-input bg-card text-foreground">
                  {masterData.transporters.find(t => t.id === formData.transporter_id)?.company_name ||
                    masterData.transporters.find(t => t.id === formData.transporter_id)?.name ||
                    "Current Transporter"}
                </div>
              </div>
            )}

            <CommonSelect
              label="Driver"
              id="driver_id"
              value={formData.driver_id}
              onValueChange={(value) => handleChange("driver_id", value)}
              options={driverOptions}
              error={errors.driver_id}
              required
              placeholder="Select driver"
              searchable
              loading={loading.drivers}
              onSearchChange={setDriverSearchTerm}
              searchValue={driverSearchTerm}
              hasMore={driversHasMore}
              loadingMore={loadingMoreDrivers}
              onLoadMore={loadMoreDrivers}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CommonSelect
              label="Vehicle"
              id="vehicle_id"
              value={formData.vehicle_id}
              onValueChange={(value) => handleChange("vehicle_id", value)}
              options={vehicleOptions}
              error={errors.vehicle_id}
              required
              placeholder="Select vehicle"
              searchable
              loading={loading.vehicles}
              onSearchChange={setVehicleSearchTerm}
              searchValue={vehicleSearchTerm}
              hasMore={vehiclesHasMore}
              loadingMore={loadingMoreVehicles}
              onLoadMore={loadMoreVehicles}
            />

            {showPortField && (
              <CommonSelect
                label="Port"
                id="port_id"
                value={formData.port_id}
                onValueChange={(value) => handleChange("port_id", value)}
                options={portOptions}
                error={errors.port_id}
                required={!['region_head', 'account_head', 'field_officer'].includes(userRole)}
                placeholder="Select port"
                searchable
              />
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DateTimeInput
              label="Scheduled Start Time"
              id="scheduled_start_time"
              value={formData.scheduled_start_time}
              onChange={(e) => handleChange("scheduled_start_time", e.target.value)}
              error={errors.scheduled_start_time}
              required
              placeholder="Select start date "
              minDate={new Date()}
              defaultToCurrentTime={true}
            />

            <DateTimeInput
              label="Scheduled End Time"
              id="scheduled_end_time"
              value={formData.scheduled_end_time}
              onChange={(e) => handleChange("scheduled_end_time", e.target.value)}
              error={errors.scheduled_end_time}
              required
              placeholder="Select end date "
              minDate={new Date()}
              defaultToCurrentTime={false}
            />
          </div>

          <div className="border rounded-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">
                {userProfile?.role === 'distributor' ? "Retailer & Products" : "Distributor & Products"}
              </h3>
              <CommonButton
                type="button"
                variant="outline"
                size="sm"
                onClick={addDistributorProductRow}
                className="flex items-center"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </CommonButton>
            </div>

            {distributorProductRows.map((row, index) => (
              <div key={row.id} className="border rounded-lg p-4 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-3 items-end">
                  <div className="md:col-span-4">
                    <CommonSelect
                      label="State"
                      id={`state_id_${row.id}`}
                      value={row.state_id}
                      onValueChange={(value) => {
                        updateDistributorProductRow(row.id, "state_id", value);
                        updateDistributorProductRow(row.id, "district_id", "");
                        updateDistributorProductRow(row.id, "distributor_id", "");
                      }}
                      options={stateOptions}
                      error={errors.distributor_product_rows?.[index]?.state_id}
                      required
                      placeholder="Select state"
                      searchable
                    />
                  </div>

                  <div className="md:col-span-4">
                    <CommonSelect
                      label="District"
                      id={`district_id_${row.id}`}
                      value={row.district_id}
                      onValueChange={(value) => {
                        updateDistributorProductRow(row.id, "district_id", value);
                        updateDistributorProductRow(row.id, "distributor_id", "");
                      }}
                      options={districtOptions}
                      error={errors.distributor_product_rows?.[index]?.district_id}
                      required
                      placeholder="Select district"
                      searchable
                      disabled={!row.state_id}
                    />
                  </div>

                  <div className="md:col-span-4">
                    <CommonSelect
                      label={userProfile?.role === 'distributor' ? "Retailer" : "Distributor"}
                      id={`distributor_id_${row.id}`}
                      value={row.distributor_id}
                      onValueChange={(value) => {
                        if (value !== 'no-data' && value !== 'loading') {
                          updateDistributorProductRow(row.id, "distributor_id", value);

                          if (userProfile?.role === 'distributor') {
                            const selectedRetailer = (masterData.retailers || []).find(r => r.id === value);
                            if (selectedRetailer) {
                              updateDistributorProductRow(row.id, "consignee_name", selectedRetailer.company_name || selectedRetailer.username || "");
                              updateDistributorProductRow(row.id, "consignee_phone", selectedRetailer.phone_number || selectedRetailer.user?.phone_number || "");
                              updateDistributorProductRow(row.id, "consignee_address", selectedRetailer.address || "");
                              if (selectedRetailer.latitude) {
                                updateDistributorProductRow(row.id, "end_latitude", selectedRetailer.latitude.toString());
                              }
                              if (selectedRetailer.longitude) {
                                updateDistributorProductRow(row.id, "end_longitude", selectedRetailer.longitude.toString());
                              }
                            }
                          }
                        }
                      }}
                      options={userProfile?.role === 'distributor'
                        ? (row.district_id
                          ? (masterData.retailers || []).filter(r => r.district_id === row.district_id || r.state_id === row.state_id).length > 0
                            ? (masterData.retailers || [])
                              .filter(r => r.district_id === row.district_id || r.state_id === row.state_id)
                              .map(r => ({
                                value: r.id,
                                label: r.company_name || r.username || r.name || r.id
                              }))
                            : [{ value: 'no-data', label: 'No retailers found', disabled: true }]
                          : [])
                        : ((row.district_id && districtDistributors[row.district_id])
                          ? districtDistributors[row.district_id].length > 0
                            ? districtDistributors[row.district_id].map(d => ({
                              value: d.id,
                              label: d.company_name || d.name || d.id
                            }))
                            : [{ value: 'no-data', label: 'No data found', disabled: true }]
                          : row.district_id
                            ? (loading.distributors
                              ? [{ value: 'loading', label: 'Loading...', disabled: true }]
                              : [{ value: 'no-data', label: 'No data found', disabled: true }])
                            : [])
                      }
                      error={errors.distributor_product_rows?.[index]?.distributor_id}
                      required
                      placeholder={row.district_id
                        ? (userProfile?.role === 'distributor' ? "Select retailer" : "Select distributor")
                        : "Select district first"
                      }
                      searchable
                      onSearch={(searchTerm) => {
                        if (userProfile?.role !== 'distributor' && row.district_id) {
                          handleDistributorSearch(row.district_id, searchTerm);
                        }
                      }}
                      disabled={!row.district_id || (userProfile?.role !== 'distributor' && loading.distributors)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-3 items-end">
                  <div className="md:col-span-6">
                    <CommonSelect
                      label="Product Type"
                      id={`product_type_id_${row.id}`}
                      value={row.product_type_id}
                      onValueChange={(value) => updateDistributorProductRow(row.id, "product_type_id", value)}
                      options={productTypeOptions}
                      error={errors.distributor_product_rows?.[index]?.product_type_id}
                      required
                      placeholder="Select product type"
                      searchable
                    />
                  </div>

                  <div className="md:col-span-6">
                    <CommonInput
                      label="Quantity"
                      id={`product_quantity_${row.id}`}
                      type="number"
                      value={row.product_quantity}
                      onChange={(e) => updateDistributorProductRow(row.id, "product_quantity", e.target.value)}
                      error={errors.distributor_product_rows?.[index]?.product_quantity}
                      required
                      placeholder="Enter quantity"
                    />
                  </div>
                </div>

                <div className="border rounded-lg p-4 mb-4">
                  <h4 className="text-md font-medium mb-4">Consignee (Ship To) Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <CommonSelect
                      label="State"
                      id={`consignee_state_id_${row.id}`}
                      value={row.consignee_state_id || ""}
                      onValueChange={(value) => {
                        updateDistributorProductRow(row.id, "consignee_state_id", value);
                        updateDistributorProductRow(row.id, "consignee_district_id", "");
                        clearRowError(row.id, "consignee_state_id", "consignee_district_id");
                        fetchConsigneeDistricts(value);
                      }}
                      options={stateOptions}
                      placeholder="Select state"
                      searchable
                      required
                      error={errors.distributor_product_rows?.[index]?.consignee_state_id}
                    />

                    <CommonSelect
                      label="District"
                      id={`consignee_district_id_${row.id}`}
                      value={row.consignee_district_id || ""}
                      onValueChange={(value) => {
                        updateDistributorProductRow(row.id, "consignee_district_id", value);
                        clearRowError(row.id, "consignee_district_id");
                      }}
                      options={
                        (row.consignee_state_id && consigneeStateDistricts[row.consignee_state_id])
                          ? consigneeStateDistricts[row.consignee_state_id].map(d => ({
                              value: d.id?.toString(),
                              label: d.district_name || d.name || d.id
                            }))
                          : []
                      }
                      placeholder={row.consignee_state_id ? "Select district" : "Select state first"}
                      searchable
                      disabled={!row.consignee_state_id || loadingConsigneeDistricts[row.consignee_state_id]}
                      required
                      error={errors.distributor_product_rows?.[index]?.consignee_district_id}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <CommonInput
                      label="Consignee Name"
                      id={`consignee_name_${row.id}`}
                      value={row.consignee_name}
                      onChange={(e) => updateDistributorProductRow(row.id, "consignee_name", e.target.value)}
                      error={errors.distributor_product_rows?.[index]?.consignee_name}
                      required
                      placeholder="Enter consignee name"
                    />

                    <CommonInput
                      label="Contact Person (Optional)"
                      id={`consignee_contact_person_${row.id}`}
                      value={row.consignee_contact_person}
                      onChange={(e) => updateDistributorProductRow(row.id, "consignee_contact_person", e.target.value)}
                      placeholder="Enter contact person name"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <CommonInput
                      label="Email (Optional)"
                      id={`consignee_email_${row.id}`}
                      value={row.consignee_email}
                      onChange={(e) => updateDistributorProductRow(row.id, "consignee_email", e.target.value)}
                      type="email"
                      placeholder="Enter email address"
                    />

                    <CommonInput
                      label="Phone Number"
                      id={`consignee_phone_${row.id}`}
                      type="tel"
                      value={row.consignee_phone}
                      onChange={(e) => {
                        const inputValue = e.target.value;

                        const sanitizedValue = inputValue.replace(/\D/g, '').slice(0, 10);
                        updateDistributorProductRow(row.id, "consignee_phone", sanitizedValue);
                      }}
                      onKeyPress={(e) => {
                        if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                          e.preventDefault();
                        }
                      }}
                      error={errors.distributor_product_rows?.[index]?.consignee_phone}
                      required
                      placeholder="Enter 10-digit phone number"
                      maxLength={10}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <CommonInput
                      label="Invoice Number"
                      id={`invoice_number_${row.id}`}
                      value={row.invoice_number}
                      onChange={(e) => updateDistributorProductRow(row.id, "invoice_number", e.target.value)}
                      error={errors.distributor_product_rows?.[index]?.invoice_number}
                      required
                      placeholder="Enter invoice number"
                    />

                    <CommonInput
                      label="DC Number"
                      id={`dc_number_${row.id}`}
                      value={row.dc_number}
                      onChange={(e) => updateDistributorProductRow(row.id, "dc_number", e.target.value)}
                      error={errors.distributor_product_rows?.[index]?.dc_number}
                      required
                      placeholder="Enter DC number"
                    />

                    <CommonInput
                      label="E-way Bill Number"
                      id={`eway_bill_number_${row.id}`}
                      value={row.eway_bill_number || ""}
                      onChange={(e) => updateDistributorProductRow(row.id, "eway_bill_number", e.target.value)}
                      error={errors.distributor_product_rows?.[index]?.eway_bill_number}
                      required
                      placeholder="Enter E-way Bill number"
                      maxLength={50}
                    />


                    <CommonInput
                      label="Estimated Kms"
                      id={`estimated_kms_${row.id}`}
                      type="number"
                      value={row.estimated_kms}
                      onChange={(e) => updateDistributorProductRow(row.id, "estimated_kms", e.target.value)}
                      error={errors.distributor_product_rows?.[index]?.estimated_kms}
                      required
                      placeholder="Enter estimated km"
                      min="0"
                      step="0.1"
                    />
                  </div>





                  <div className="mt-4">
                    <CommonInput
                      label="Address"
                      id={`consignee_address_${row.id}`}
                      value={row.consignee_address}
                      onChange={(e) => updateDistributorProductRow(row.id, "consignee_address", e.target.value)}
                      error={errors.distributor_product_rows?.[index]?.consignee_address}
                      required
                      placeholder="Enter full address"
                    />
                  </div>

                  <div className="mt-4">
                    <div className="mb-2">
                      <label className="text-sm font-medium text-gray-700">End Location *</label>
                    </div>
                    <GoogleMapSelector
                      onLocationSelect={(location) => {
                        updateDistributorProductRow(row.id, "end_latitude", location.lat.toString());
                        updateDistributorProductRow(row.id, "end_longitude", location.lng.toString());
                      }}
                      initialLat={row.end_latitude ? parseFloat(row.end_latitude) : null}
                      initialLng={row.end_longitude ? parseFloat(row.end_longitude) : null}
                    />
                    {errors.distributor_product_rows?.[index]?.end_location && (
                      <p className="text-sm text-destructive mt-1">
                        {errors.distributor_product_rows?.[index]?.end_location}
                      </p>
                    )}

                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border rounded-lg p-4">
                    <CommonFileUpload
                      label="Upload Eway Bill"
                      id={`trip_photo_${row.id}`}
                      accept=".pdf,.doc,.docx,.xls,.xlsx"
                      onFileSelect={(file) => handleRowFileChange(row.id, file)}
                      error={errors.distributor_product_rows?.[index]?.trip_photo}
                      required
                    />
                  </div>

                  <div className="border rounded-lg p-4">
                    <CommonFileUpload
                      label="Upload Invoice"
                      id={`invoice_file_${row.id}`}
                      accept=".pdf,.doc,.docx,.xls,.xlsx"
                      onFileSelect={(file) => handleRowInvoiceFileChange(row.id, file)}
                      error={errors.distributor_product_rows?.[index]?.invoice_file}
                      required
                    />
                  </div>
                </div>


                <div className="flex justify-end mt-3">
                  {distributorProductRows.length > 1 && (
                    <CommonButton
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 p-0"
                      onClick={() => removeDistributorProductRow(row.id)}
                    >
                      <X className="h-4 w-4" />
                    </CommonButton>
                  )}
                </div>

              </div>
            ))}

            {errors.distributor_product_rows && typeof errors.distributor_product_rows === 'string' && (
              <p className="text-sm text-destructive mt-1">
                {errors.distributor_product_rows}
              </p>
            )}
          </div>

          <div className="flex justify-end space-x-2 pt-5">
            <CommonButton
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </CommonButton>
            <CommonButton
              type="submit"
              variant="default"
              loading={isSubmitting || internalSubmitting}
            >
              Create Trip
            </CommonButton>
          </div>
        </div>
      </div>
    </form>
  );
};

export default AddTripForm;